import { ThreadDetailPanel } from "../components/threads/ThreadDetailPanel.tsx";
import { useTopicThread } from "../hooks/useThreads.tsx";
import { useEmailAutoAction } from "./useEmailAutoAction.ts";
import { type ReactNode, useEffect, useRef } from "react";
import { useRightSidebar } from "@/components/right-sidebar/hooks.tsx";

/**
 * A thread: the conversation at `/threads/:id`, rendered in the column the
 * destinations share. Selecting one in the sidebar addresses it rather than
 * navigating out of the shell, so the list it was picked from stays beside it —
 * and a link, a notification, or a reload of that URL lands on the same screen.
 *
 * This is the only thread surface on web. It replaced a standalone page that
 * owned its own split and its own calendar canvas; two surfaces for one URL is
 * what made a thread the one destination that cost the shell.
 *
 * The conversation only. The host owns the canvas column beside it and drives
 * that from this thread's own `useThreadCanvas`, so an artifact the agent
 * presents lands in the column rather than as a card over the messages. Owning
 * a second copy here would give the screen two canvases for one thread.
 *
 * Keyed on the thread by its host, so switching threads is a fresh mount rather
 * than a prop change. That is what makes the sidebar ownership below mean
 * anything.
 *
 * `showCanvas` is false on the panel: the host's header carries the one canvas
 * toggle, and a second in this header would be a second control for one column.
 */
export const ThreadCanvasView = ({
  threadId,
  onLeave,
  parentLabel,
  leading,
}: {
  threadId: number;
  /**
   * Puts the canvas back to the view it was showing before this thread.
   * `replace` drops this thread's address rather than stacking the destination
   * on top of it — for leaving a conversation there is no going back to.
   */
  onLeave: (options?: { replace?: boolean }) => void;
  /**
   * The list this conversation was drilled into from, named in the header as
   * the way back. Omitted when it was picked from the sidebar rather than from
   * something on the canvas — there is no trail to show for a view that was
   * never the parent.
   */
  parentLabel?: string;
  leading?: ReactNode;
}) => {
  const { thread, isLoading, error, isThreadGone } = useTopicThread({ threadId });

  // A thread the server no longer resolves has no screen to be: deleted from
  // its row in the list beside this one, deleted in another tab, or an address
  // that outlived the account it belonged to. Without this the column sits on
  // it rendering the request's raw error, which is where a delete used to leave
  // the user (BIG-7845). Leaving replaces the entry, so Back doesn't walk into
  // the same dead address.
  useEffect(() => {
    if (isThreadGone) onLeave({ replace: true });
  }, [isThreadGone, onLeave]);

  // One-click action from the approval email (`?act=…`); BIG-5585. It rides the
  // thread's own URL, so it belongs to whatever renders that URL — which is this
  // now that the standalone page is gone.
  useEmailAutoAction(threadId);

  // A sidebar opened from inside this conversation belongs to it — its
  // participants, the contact it mentioned. Leaving takes the sidebar with it:
  // the canvas leaves the URL alone, so nothing else drops the `sb_` params, and
  // the sidebar would outlive the thread it came from.
  //
  // Only one this conversation opened. A sidebar already up when the thread was
  // selected came from somewhere else — the calendar, the contacts view — and is
  // not this one's to close, so ownership is claimed on a closed→open
  // transition rather than on merely finding it open.
  const { isOpen: isSidebarOpen, closeSidebar } = useRightSidebar();
  const wasSidebarOpen = useRef(isSidebarOpen);
  const ownsSidebar = useRef(false);
  useEffect(() => {
    if (isSidebarOpen && !wasSidebarOpen.current) ownsSidebar.current = true;
    if (!isSidebarOpen) ownsSidebar.current = false;
    wasSidebarOpen.current = isSidebarOpen;
  }, [isSidebarOpen]);
  // Through a ref so the cleanup below runs once, on unmount, rather than on
  // every render that hands it a new callback.
  const closeSidebarRef = useRef(closeSidebar);
  closeSidebarRef.current = closeSidebar;
  useEffect(
    () => () => {
      if (!ownsSidebar.current) return;
      // Only while those params are still in the URL. This runs *because* the
      // route changed, and the URL writer this component's `closeSidebar` is
      // bound to last saw the old location — it re-renders to learn the new
      // one, and an unmounting component never does. A navigation that went
      // through the writer is kept for it to build on, precisely for this
      // cleanup; one that did not — a Link, the Back button — would have it
      // stamp the old page's path back over the one that just replaced it:
      // leaving a thread through the settings menu once landed on
      // `/settings/scheduling?thread=42`. Such a route change has already
      // dropped the sidebar's params, which leaves nothing here to close.
      if (!window.location.search.includes("sb_")) return;
      closeSidebarRef.current();
    },
    [],
  );

  return (
    <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
      <ThreadDetailPanel
        threadId={threadId}
        thread={thread}
        isLoading={isLoading}
        error={error}
        showCanvas={false}
        onLeave={onLeave}
        parentLabel={parentLabel}
        leading={leading}
      />
    </div>
  );
};
